import { useEffect, useState } from 'react';

/**
 * Scroll-spy: ritorna l'id della sezione "principalmente in view" in base a
 * IntersectionObserver. Pattern usato da `BuilderPage` per pilotare lo stato
 * `current` di `<StepRail>` + `<StepNav>` mentre l'utente scrolla.
 *
 * Convenzione: ogni sezione deve avere `id="section-<sectionId>"` nel DOM.
 * L'hook accetta direttamente i `sectionId` (es. `'class'`, `'race'`, ...) e
 * costruisce internamente i selettori.
 *
 * Threshold: `rootMargin: '-30% 0px -50% 0px'` — una sezione è "in view" quando
 * la sua banda dal 30% al 50% della viewport è attraversata. Pattern classico
 * scroll-spy: stabile alle "due sezioni visibili insieme".
 *
 * Fallback: se nessuna sezione è in view (es. utente sopra la prima sezione,
 * o tutti gli elementi rimossi), ritorna il primo id passato.
 *
 * SSR / test safety: se `IntersectionObserver` non è disponibile (es. jsdom
 * senza polyfill), ritorna sempre il primo id (no-op + fallback).
 */
/**
 * @param sectionIds ID delle sezioni da osservare (ordine = ordine DOM).
 * @param remountKey opzionale. Quando cambia, il hook RIESEGUE `useEffect` e
 *   riosserva gli elementi DOM. Necessario quando il parent monta le sezioni
 *   con `key={...}` per resettarle (es. `BuilderPage` con `key={draft.id}` post
 *   `RESET_DRAFT`): senza questo trigger l'observer continuerebbe a osservare
 *   i vecchi nodi rimossi dal DOM. **FI-68** (regressione di FI-65).
 */
export function useSectionInView(
  sectionIds: readonly string[],
  remountKey?: string | number,
): string | null {
  const [currentId, setCurrentId] = useState<string | null>(() => sectionIds[0] ?? null);

  useEffect(() => {
    if (sectionIds.length === 0) {
      setCurrentId(null);
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setCurrentId(sectionIds[0] ?? null);
      return;
    }

    const elements: HTMLElement[] = [];
    const visibility = new Map<string, number>();

    for (const id of sectionIds) {
      const el = document.getElementById(`section-${id}`);
      if (el) elements.push(el);
      visibility.set(id, 0);
    }

    function recompute(): void {
      // **FI-66**: fallback near-bottom-of-page per l'ultima sezione troppo
      // corta per attraversare la banda observer `-30% 0px -50% 0px`. Esempio
      // builder: sezione `identity-bonds` con `bondsDeferred=true` mostra solo
      // 2 righe + badge — altezza < 20% viewport → mai dentro la banda 30-50%
      // → `currentId` resta sulla penultima sezione (spells) → StepRail/StepNav
      // disallineati con la scroll position reale.
      const docHeight = document.documentElement.scrollHeight;
      const scrollBottom = window.innerHeight + window.scrollY;
      // Tolleranza 4px per arrotondamenti subpixel del browser.
      if (scrollBottom >= docHeight - 4) {
        const last = sectionIds[sectionIds.length - 1];
        if (last !== undefined) {
          setCurrentId(last);
          return;
        }
      }
      const firstVisible = sectionIds.find((id) => (visibility.get(id) ?? 0) > 0);
      setCurrentId(firstVisible ?? sectionIds[0] ?? null);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id.replace(/^section-/, '');
          visibility.set(id, entry.isIntersecting ? entry.intersectionRatio || 1 : 0);
        }
        recompute();
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: 0 },
    );

    for (const el of elements) observer.observe(el);

    // **FI-66**: scroll listener per il fallback near-bottom. L'observer non
    // si attiva se l'utente scrolla DENTRO l'ultima sezione corta (nessuna
    // entry cambia `isIntersecting`); senza questo listener il fallback non
    // verrebbe mai eseguito.
    window.addEventListener('scroll', recompute, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', recompute);
    };
    // `remountKey` in deps: rieffettua l'observe quando il parent rimonta le
    // sezioni (FI-68). `sectionIds` è module-constant nel caller corrente, ma
    // resta in deps per safety se in futuro il caller le rende reactive.
  }, [sectionIds, remountKey]);

  return currentId;
}
